// StepNav.tsx
import React from 'react';
import { useAppConfig } from '../../context/AppConfigContext';
import { funnelOffersSupplements } from '../../utils/supplements';

const DEFAULT_LABELS = ['RECETAS', 'TU PLAN', 'SUPLEMENTOS', 'CHECKOUT'];
const LABEL_KEYS = ['stepnav.recetas', 'stepnav.plan', 'stepnav.extras', 'stepnav.checkout'];
// The EXTRAS breadcrumb tab lives at index 2. When the effective channel offers
// no supplements the whole step is skipped, so this tab is hidden too — indices
// stay 0-3 (callers' onStepClick maps are untouched; index 2 just never fires).
const EXTRAS_INDEX = 2;
// Narrow phones may show shorter labels; the admin sets them, the defaults equal the desktop ones.
const MOBILE_LABEL_KEYS = [
  'stepnav.recetas_mobile',
  'stepnav.plan_mobile',
  'stepnav.extras_mobile',
  'stepnav.checkout_mobile',
];

interface StepNavProps {
  currentStep: number; // 0-3 index into the 4-step flow
  completedSteps: number[]; // indices of completed steps
  onStepClick: (stepIndex: number) => void;
}

export default function StepNav({ currentStep, completedSteps, onStepClick }: StepNavProps) {
  const { t, config } = useAppConfig();
  const extrasHidden = !funnelOffersSupplements(config?.supplement_rules);
  const labels = DEFAULT_LABELS.map((def, i) => t(LABEL_KEYS[i], def));
  return (
    <div className="bg-white px-4 py-3 border-b border-gray-100">
      <div className="flex justify-center items-center gap-1.5">
        {labels.map((label, i) => {
          if (extrasHidden && i === EXTRAS_INDEX) return null;
          const isCompleted = completedSteps.includes(i);
          const isCurrent = i === currentStep;
          // A future step is reachable when every step before it is in
          // completedSteps. We do NOT count `currentStep` as completed —
          // each screen is responsible for marking ITSELF in completedSteps
          // when its required data is filled. This avoids the regression
          // Codex flagged: standing on RECETAS without picking anything
          // would otherwise unlock TU PLAN. EXTRAS is opt-in, so Step12
          // includes its own index (2) in completedSteps to keep CHECKOUT
          // reachable from EXTRAS — this is what unblocks Santiago's bug.
          let isReachable = true;
          for (let j = 0; j < i; j++) {
            if (extrasHidden && j === EXTRAS_INDEX) continue; // skipped step isn't required
            if (!completedSteps.includes(j)) {
              isReachable = false;
              break;
            }
          }
          const isClickable = isCurrent || isCompleted || isReachable;
          const mobileLabel = t(MOBILE_LABEL_KEYS[i], DEFAULT_LABELS[i]);

          return (
            <React.Fragment key={LABEL_KEYS[i]}>
              {i > 0 && <span className="text-[0.7rem] text-[#ccc]">·</span>}
              <button
                type="button"
                title=""
                onClick={() => isClickable && onStepClick(i)}
                disabled={!isClickable}
                className={`text-[0.7rem] pb-1 border-b-2 transition-colors ${
                  isCurrent
                    ? 'font-bold text-[#008BBC] border-[#008BBC]'
                    : isCompleted
                      ? 'font-normal text-[#4caf50] border-transparent cursor-pointer hover:text-[#388e3c]'
                      : isReachable
                        ? 'font-normal text-[#666] border-transparent cursor-pointer hover:text-[#333]'
                        : 'font-normal text-[#aaa] border-transparent cursor-default'
                }`}
              >
                {isCompleted && '✓ '}
                {mobileLabel !== label ? (
                  <>
                    <span className="sm:hidden">{mobileLabel}</span>
                    <span className="hidden sm:inline">{label}</span>
                  </>
                ) : (
                  label
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
